import React, { useState } from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useRowSelect,
} from "react-table";

import classes from "./index.module.scss";
import { Col, Row, Form } from "react-bootstrap";
const SampleDataTable = ({
  columns,
  data,
  style,
  maxWidth,
  center,
  onSelectionChange,
  noDataMsg,
  isLoading,
  resultsTitle = "Total Tickets Found",
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  const handleCheckboxChange = (id) => {
    const isSelected = selectedIds.includes(id);

    if (isSelected) {
      setSelectedIds((prevIds) => prevIds.filter((prevId) => prevId !== id));
    } else {
      setSelectedIds((prevIds) => [...prevIds, id]);
    }
  };

  const extendedColumns = React.useMemo(() => {
    if (onSelectionChange !== undefined)
      // Append the checkbox column to the provided columns
      return [
        {
          id: "selection",
          disableSortBy: true, // ✅ NEW: do not sort on checkbox column
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <input
              type="checkbox"
              onChange={(e) => {
                // Prevent sorting when clicking the checkbox
                e.stopPropagation();
                const allRowsSelected = e.target.checked;
                if (allRowsSelected) {
                  setSelectedIds(data.map((row) => row.id));
                } else {
                  setSelectedIds([]);
                }
              }}
              checked={selectedIds.length === data.length && data.length > 0}
            />
          ),
          Cell: ({ row }) => (
            <input
              type="checkbox"
              onChange={(e) => {
                e.stopPropagation(); // ✅ NEW: prevent sorting if click bubbles
                handleCheckboxChange(row.original.id);
              }}
              checked={selectedIds.includes(row.original.id)}
            />
          ),
          accessor: "selection",
        },
        ...columns,
      ];
    else
      return [
        {
          id: "selection",
          disableSortBy: true, // ✅ NEW: do not sort on Sr. column
          Header: () => <p className="mb-0"> Sr.</p>,
          Cell: ({ row, index }) => <p>{index}</p>,
          accessor: "selection",
        },
        ...columns,
      ];
  }, [columns, data, selectedIds, onSelectionChange]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    gotoPage,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex, pageSize },
    setPageSize,
  } = useTable(
    {
      columns: extendedColumns,
      data,

      // ✅ NEW: allow removing sort (go back to original order)
      disableSortRemove: false,

      // ✅ keep your default page size
      initialState: {
        pageSize: 300,
        sortBy: [], // ✅ start unsorted
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const { globalFilter } = state;

  React.useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedIds);
  }, [onSelectionChange, selectedIds]);

  // ✅ NEW: custom sort handler => ASC -> UNSORT (no DESC)
  const handleHeaderSortClick = (column, e) => {
    e.stopPropagation();

    if (!column.canSort) return;

    // If currently sorted ASC => remove sorting (back to original)
    if (column.isSorted && column.isSortedDesc === false) {
      column.clearSortBy();
      return;
    }

    // Otherwise always set ASC (desc=false) and single-sort (multi=false)
    column.toggleSortBy(false, false);
  };

  return (
    <div>
      <Row className="">
        <Col md={3}>
          {data.length > 0 ? (
            <Form.Label style={{ fontSize: "14px", color: "#000" }}>
              {resultsTitle}{" "}
              <span style={{ color: "#757575", fontWeight: "bold" }}>
                {data?.length}
              </span>
            </Form.Label>
          ) : (
            <Form.Label style={{ fontSize: "14px", color: "#000" }}>
              {isLoading ? "Loading..." : noDataMsg || "No Tickets Found"}
            </Form.Label>
          )}
        </Col>
      </Row>

      <div style={{ overflowX: "auto" }}>
        <table className="simpleTable" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                  // ✅ NEW: get header props (no default toggle props)
                  const headerProps = column.getHeaderProps();

                  return (
                    <th
                      {...headerProps}
                      onClick={(e) => handleHeaderSortClick(column, e)}
                      style={{
                        cursor: column.canSort ? "pointer" : "default",
                        userSelect: "none",
                      }}
                    >
                      <span style={{ display: "inline-flex", gap: 6 }}>
                        {column.render("Header")}

                        {/* ✅ NEW: show arrow only when sorted ASC */}
                        {column.canSort && column.isSorted && !column.isSortedDesc ? (
                          <span aria-label="sorted-asc">🔼</span>
                        ) : null}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, rowNum) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell, index) => (
                    <td {...cell.getCellProps()} key={index}>
                      {index === 0 && onSelectionChange !== undefined ? (
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(row.original.id);
                          }}
                          checked={selectedIds.includes(row.original.id)}
                        />
                      ) : onSelectionChange === undefined && index === 0 ? (
                        <p>{rowNum + 1}</p>
                      ) : (
                        cell.render("Cell")
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex align-items-center justify-content-between flex-wrap py-3">
        <div>
          <span className="me-4">
            Page
            <strong className="ms-2">
              {pageIndex + 1} of {pageOptions.length}
            </strong>
          </span>
          <span>
            <span>
              Show
              <select
                value={pageSize}
                className="mx-2"
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
              >
                {[300, 600, 900].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
              entries
            </span>
          </span>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <span className="me-2">Go to page:</span>
          <input
            type="number"
            style={{
              width: "50px",
              border: "1px solid #ccc",
              padding: "4px 0",
            }}
            defaultValue={pageIndex + 1}
            className="text-center"
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
          />
          <button
            className="btn btn-table"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
          >
            {"<<"}
          </button>
          <button
            className="btn btn-table"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            {"<"}
          </button>

          <button
            className="btn btn-table"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            {">"}
          </button>
          <button
            className="btn btn-table"
            onClick={() => gotoPage(pageOptions.length - 1)}
            disabled={!canNextPage}
          >
            {">>"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SampleDataTable;